import React from 'react';
import type {
  ProductCarouselNavigationControl,
  ProductCarouselProduct,
} from '@noths/polaris-client-ribbons-design-system';
import { ProductCarousel } from '@noths/polaris-client-ribbons-design-system';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { useRecommendationsServiceProductData } from '../../../hooks/useRecommendationsServiceProductData/useRecommendationsServiceProductData';
import type { RecommendationsServiceProduct } from '../../../services/recommendations';
import type { RecommendedItem } from '../../../types/recommendations';

export const testId = 'ProductCarouselWithRecommendationsServiceDataTestId';

interface ProductCarouselWithRecommendationsServiceDataOnNavigatePayload {
  numberOfItems: number;
  strategy: string;
}

interface ProductCarouselWithRecommendationsServiceDataOnVisiblePayload {
  products: RecommendationsServiceProduct[];
  strategy: string;
}

interface ProductCarouselWithRecommendationsServiceDataOnProductClickPayload {
  index: number;
  product: RecommendationsServiceProduct;
  numberOfItems: number;
  strategy: string;
  recommendationsTrackingUrl: string;
}

export interface ProductCarouselWithRecommendationsServiceDataProps {
  currency: CurrencyCode;
  items?: RecommendedItem[];
  onNavigateCarousel?: (
    swiped: boolean,
    control: ProductCarouselNavigationControl,
    payload: ProductCarouselWithRecommendationsServiceDataOnNavigatePayload,
  ) => void;
  onVisible?: (payload: ProductCarouselWithRecommendationsServiceDataOnVisiblePayload) => void;
  onProductClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    payload: ProductCarouselWithRecommendationsServiceDataOnProductClickPayload,
  ) => void;
  placementTitle?: string;
  strategy?: string;
}

const transformRecommendationsServiceProductToProductCarouselProduct = (
  {
    title,
    images,
    links,
    purchasable: isPurchasable,
    new: isNew,
    on_sale: onSale,
    prices,
    sale_percentage,
    pre_sale_prices,
  }: RecommendationsServiceProduct,
  currency: CurrencyCode,
  linkUrl: string,
): ProductCarouselProduct => ({
  title,
  src: images[0]?.href || '',
  href: links[0]?.href || '',
  alt: '',
  isNew,
  onSale,
  isPurchasable,
  price: prices?.find((prices) => prices.currency === currency),
  preSalePrice: pre_sale_prices?.find((price) => price.currency === currency),
  salePercentage: sale_percentage ?? undefined,
  trackingURL: linkUrl,
});

export const ProductCarouselWithRecommendationsServiceData = (
  props: ProductCarouselWithRecommendationsServiceDataProps,
) => {
  const {
    currency,
    items = [],
    onNavigateCarousel,
    placementTitle = '',
    strategy = '',
    onVisible,
    onProductClick,
  } = props;
  const [products, loaded] = useRecommendationsServiceProductData(items);
  const renderProducts = products?.length > 0;

  return !loaded || (loaded && renderProducts) ? (
    <div data-testid={testId}>
      <ProductCarousel
        isLoading={!renderProducts}
        products={products.map((product) =>
          transformRecommendationsServiceProductToProductCarouselProduct(
            product.product,
            currency,
            product.linkURL,
          ),
        )}
        title={placementTitle}
        onVisible={() => {
          const recommendationsServiceProducts = products.map((product) => product.product);
          onVisible?.({ products: recommendationsServiceProducts, strategy });
        }}
        onNavigation={(swiped, control) => {
          onNavigateCarousel?.(swiped, control, { numberOfItems: products.length, strategy });
        }}
        onProductClick={(evt, { index }) => {
          onProductClick?.(evt, {
            product: products[index].product,
            recommendationsTrackingUrl: products[index].linkURL,
            index,
            numberOfItems: products.length,
            strategy,
          });
        }}
      />
    </div>
  ) : null;
};
